import { serializeQuestionnaireJsonObject } from '@risksmart-app/components/src/form-builder/questionnaireSchemaContracts';
import { VersionStatus as Version_Status_Enum } from '@risksmart-app/domain/src/types/consts/version-status';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import ConfirmModal from 'src/components/confirm-modal/ConfirmModal';

import { useInsertQuestionnaireTemplateVersion } from '@/hooks/mutations/questionnaire-template-version';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { useIsFormPrefillEnabled } from '@/hooks/useIsFormPrefillEnabled';

import QuestionnaireVersionTemplateForm from '../../../forms/QuestionnaireTemplateVersionForm';
import type { QuestionnaireTemplateVersionFormFieldData } from '../../../forms/questionnaireTemplateVersionSchema';
import { defaultValues } from '../../../forms/questionnaireTemplateVersionSchema';
import { useNextTemplateVersion } from '../../../useNextTemplateVersion';

type TabProps = {
  parentId: string;
};

const Tab = ({ parentId }: TabProps) => {
  const { t: qt } = useTranslation(['common'], {
    keyPrefix: 'questionnaire_template_versions.confirm_close_modal',
  });

  const [confirmCloseVisible, setConfirmCloseVisible] = useState(false);
  const { insertQuestionnaireTemplateVersion } =
    useInsertQuestionnaireTemplateVersion();
  const objectToolbarEnabled = useIsFeatureFlagEnabled('dynamic_forms');
  const formPrefillEnabled = useIsFormPrefillEnabled();

  const [
    { nextVersion, schema, uiSchema },
    { loading: loadingNextTemplateVersion },
  ] = useNextTemplateVersion(parentId);

  const navigate = useNavigate();

  const onSave = async (data: QuestionnaireTemplateVersionFormFieldData) => {
    await insertQuestionnaireTemplateVersion({
      Version: data.Version,
      Schema: serializeQuestionnaireJsonObject(data.Schema),
      UISchema: serializeQuestionnaireJsonObject(data.UISchema),
      ParentId: parentId,
      CustomAttributeData: data.CustomAttributeData ?? null,
    });
  };

  const defaultValuesWithVersion: QuestionnaireTemplateVersionFormFieldData = {
    ...defaultValues,
    Version: nextVersion,
    Schema: schema,
    UISchema: uiSchema,
  };

  if (loadingNextTemplateVersion) {
    return null;
  }

  const onDismiss = () => {
    navigate(`/third-party/questionnaire/${parentId}/versions`);
  };

  return (
    <>
      <QuestionnaireVersionTemplateForm
        disableStatus={true}
        onSave={onSave}
        onDismiss={(saved) => {
          if (saved) {
            onDismiss();
          } else {
            setConfirmCloseVisible(true);
          }
        }}
        parentId={parentId}
        savedStatus={Version_Status_Enum.Draft}
        isCreatingNewEntity={false}
        defaultValues={defaultValuesWithVersion}
        publishToolbarState={objectToolbarEnabled}
        publishActiveForm={formPrefillEnabled}
      />

      <ConfirmModal
        isVisible={confirmCloseVisible}
        onConfirm={onDismiss}
        onDismiss={() => setConfirmCloseVisible(false)}
        header={qt('title')}
      >
        {qt('message')}
      </ConfirmModal>
    </>
  );
};

export default Tab;
